import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Mail, MapPin, Clock, CheckCircle2, AlertCircle, Send } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';
import { AUTHOR, ORG } from '@/config/authorProfile';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: 'General inquiry', message: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: 'error', msg: 'Name, email, and message are required.' });
      return;
    }
    setStatus({ state: 'sending', msg: '' });
    try {
      const resp = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || 'Something went wrong sending your message.');
      }
      setStatus({
        state: 'ok',
        msg: `Message received. We'll reply ${AUTHOR.responseTimePromise}.`,
      });
      setForm({ name: '', email: '', subject: 'General inquiry', message: '' });
    } catch (err) {
      setStatus({ state: 'error', msg: err.message || 'Failed to send. Please email us directly.' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <AppLogo size={36} />
            <p className="heading text-sm font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </p>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16 max-w-3xl">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-8"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-4">
            Contact
          </p>
          <h1 className="heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
            Get in touch
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            Questions, corrections, partnership inquiries, or media requests —
            we read everything. Replies typically arrive {AUTHOR.responseTimePromise}.
          </p>
        </motion.section>

        <div className="grid md:grid-cols-[1fr_1.4fr] gap-6">
          {/* Direct channels */}
          <Card className="border-slate-200 bg-white shadow-sm h-fit" data-testid="contact-channels">
            <CardContent className="p-6 space-y-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                  Email
                </p>
                <a
                  href={`mailto:${AUTHOR.email}`}
                  className="text-sm text-slate-900 font-medium hover:text-blue-600 inline-flex items-center gap-2"
                  data-testid="contact-email-link"
                >
                  <Mail className="w-4 h-4" /> {AUTHOR.email}
                </a>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                  Response time
                </p>
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  {AUTHOR.responseTimePromise}
                </p>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                  Mailing address
                </p>
                <address className="text-sm text-slate-700 not-italic leading-relaxed">
                  <MapPin className="w-4 h-4 text-slate-400 inline mr-1 -mt-0.5" />
                  {ORG.mailingAddress.line1}<br />
                  {ORG.mailingAddress.line2}<br />
                  {ORG.mailingAddress.city}, {ORG.mailingAddress.region}<br />
                  {ORG.mailingAddress.country}
                </address>
              </div>

              <div className="pt-3 border-t border-slate-100">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                  What to send where
                </p>
                <ul className="text-xs text-slate-600 space-y-1.5">
                  <li><span className="font-semibold text-slate-800">Corrections:</span> "Correction" in subject</li>
                  <li><span className="font-semibold text-slate-800">Media:</span> "Press" in subject</li>
                  <li><span className="font-semibold text-slate-800">Partnerships:</span> "Partner" in subject</li>
                  <li><span className="font-semibold text-slate-800">Bugs:</span> include URL + browser</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Form */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700 mb-1 block">Name</span>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="contact-form-name"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700 mb-1 block">Email</span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="contact-form-email"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 mb-1 block">Subject</span>
                  <select
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    data-testid="contact-form-subject"
                  >
                    <option>General inquiry</option>
                    <option>Correction</option>
                    <option>Press / media</option>
                    <option>Partnership</option>
                    <option>Bug report</option>
                    <option>Data / API question</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 mb-1 block">Message</span>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                    placeholder="Tell us what you're thinking..."
                    data-testid="contact-form-message"
                  />
                </label>

                {status.state === 'ok' && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800" data-testid="contact-form-success">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{status.msg}</span>
                  </div>
                )}
                {status.state === 'error' && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800" data-testid="contact-form-error">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{status.msg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status.state === 'sending'}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white rounded-md text-sm font-medium transition-colors"
                  data-testid="contact-form-submit"
                >
                  <Send className="w-4 h-4" />
                  {status.state === 'sending' ? 'Sending…' : 'Send message'}
                </button>
                <p className="text-[11px] text-slate-500">
                  We use your info only to reply. See our{' '}
                  <Link to="/privacy" className="underline">privacy policy</Link>.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
